/**
 * Device Context Provider
 * Manages device registration and subscription state
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getMachineFingerprint, getUserInfo } from '../common/device';
import { fetchDeviceSubscription } from './SubscriptionService';
import { DeviceContextType, DeviceSubscription, Company, UserInfo } from './DeviceTypes';
import { config } from '../config/environment';

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

interface DeviceProviderProps {
  children: React.ReactNode;
}

/**
 * Device Context Provider Component
 * Initializes device fingerprint and fetches subscription on mount
 */
export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const [fingerprint, setFingerprint] = useState<string>('');
  const [subscription, setSubscription] = useState<DeviceSubscription | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Użyj ref do śledzenia czy inicjalizacja już się odbyła
  const isInitializedRef = useRef(false);

  /**
   * Initialize device and fetch subscription
   */
  const initializeDevice = useCallback(async () => {
    // Jeśli już zainicjalizowano, pomiń
    if (isInitializedRef.current) {
      console.log('⏭️ DeviceContext already initialized, skipping...');
      return;
    }

    isInitializedRef.current = true;

    console.log('🔧 DeviceContext: Initializing device...');
    console.log('🔧 API Base URL:', config.api.baseUrl);
    console.log('🔧 Environment:', config.env);

    try {
      setIsLoading(true);
      setError(null);

      // Step 1: Get user info from Office SSO
      console.log('👤 Getting user information from Office SSO...');
      const userInfoFromSSO = await getUserInfo();

      setUserInfo({
        userId: userInfoFromSSO.userId,
        email: userInfoFromSSO.email,
        name: userInfoFromSSO.name
      });

      if (userInfoFromSSO.userId) {
        console.log('✅ User info retrieved:', {
          userId: userInfoFromSSO.userId.substring(0, 8) + '...',
          email: userInfoFromSSO.email,
          name: userInfoFromSSO.name
        });
      } else {
        console.log('ℹ️ No user info available (standalone/volume license or not signed in)');
      }

      // Step 2: Generate device fingerprint
      console.log('🔑 Generating device fingerprint...');
      const deviceFingerprint = await getMachineFingerprint();
      setFingerprint(deviceFingerprint);

      // Save to localStorage for dialog access
      localStorage.setItem('deviceFingerprint', deviceFingerprint);
      localStorage.setItem('config', JSON.stringify({
        api: {
          baseUrl: config.api.baseUrl
        }
      }));

      console.log('✅ Device fingerprint generated:', deviceFingerprint.substring(0, 16) + '...');

      // Step 3: Fetch subscription data from API (always returns subscription, Trial if not registered)
      console.log('📡 Fetching subscription data...');
      const subscriptionData = await fetchDeviceSubscription(deviceFingerprint);

      setSubscription(subscriptionData);

      console.log('📦 DeviceContext: Subscription data stored in state:', {
        subscriptionId: subscriptionData.subscriptionId,
        status: subscriptionData.status,
        companiesCount: subscriptionData.companies?.length || 0,
        companiesStructure: subscriptionData.companies?.map(c => ({
          id: c.companyId,
          name: c.name,
          eupsCount: c.eups?.length || 0,
          hasEups: !!c.eups
        }))
      });

      if (subscriptionData.status === 'Trial') {
        console.log('✨ Device running in TRIAL mode (unlimited)');
      } else {
        console.log('✅ Device registered. Subscription loaded:', {
          subscriptionId: subscriptionData.subscriptionId,
          status: subscriptionData.status,
          companies: subscriptionData.companies.length,
        });
      }

      console.log('✅ DeviceContext initialization complete');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('❌ Device initialization failed:', errorMessage);
      setError(errorMessage);

      // Even on error, create Trial subscription to not block the app
      const deviceFingerprint = fingerprint || 'unknown';
      const trialSubscription: DeviceSubscription = {
        subscriptionId: `trial-error-${deviceFingerprint.substring(0, 8)}`,
        source: 'Portal',
        planId: 'carbowaste_bdo_trial',
        status: 'Trial',
        autoRenew: false,
        termUnit: 'Unlimited',
        purchaserEmail: 'trial@carbowaste.com',
        startDate: new Date().toISOString(),
        endDate: '9999-12-31T23:59:59Z',
        companies: [],
        limits: { companiesLimit: 1, eupsLimit: 9000 }
      };
      setSubscription(trialSubscription);
      console.log('⚠️ Error occurred, but continuing in TRIAL mode');
    } finally {
      setIsLoading(false);
    }
  }, []); // Usuń fingerprint z dependencies

  /**
   * Initialize on mount - tylko raz
   */
  useEffect(() => {
    initializeDevice();
  }, [initializeDevice]);

  /**
   * Refresh subscription data
   */
  const refreshSubscription = useCallback(async () => {
    console.log('🔄 Refreshing subscription...');
    try {
      // Only refresh subscription data, don't re-initialize device
      if (!fingerprint) {
        console.warn('⚠️ Cannot refresh subscription: no fingerprint');
        return;
      }

      const subscriptionData = await fetchDeviceSubscription(fingerprint);

      // CRITICAL: Clear all localStorage settings synchronously BEFORE updating subscription
      // This ensures selectedCompanyId and selectedEupId don't persist from old subscription
      console.log('🧹 Clearing localStorage settings before subscription update');
      try {
        localStorage.removeItem('carbowaste_selected_company_id');
        localStorage.removeItem('carbowaste_selected_eup_id');
        console.log('✅ localStorage cleared');
      } catch (error) {
        console.warn('⚠️ Failed to clear localStorage:', error);
      }

      // Emit custom event to notify App component that subscription is changing
      // This ensures App's state is reset synchronously before new subscription is applied
      console.log('📢 Dispatching subscription-change event');
      window.dispatchEvent(new CustomEvent('carbowaste:subscription-change', {
        detail: { subscriptionData }
      }));

      // Now update subscription - this will trigger auto-fix in App.tsx
      setSubscription(subscriptionData);

      console.log('✅ Subscription refreshed:', {
        status: subscriptionData.status,
        companies: subscriptionData.companies.length,
      });
    } catch (err) {
      console.error('❌ Failed to refresh subscription:', err);
      // Don't throw error, keep existing subscription
    }
  }, [fingerprint]);

  /**
   * Add company to Trial subscription (local only, not persisted to backend)
   */
  const addCompanyToTrialSubscription = useCallback((company: Company) => {
    console.log('➕ Adding company to Trial subscription locally:', {
      name: company.name,
      companyId: company.companyId,
      eupsCount: company.eups?.length || 0,
      eups: company.eups?.map(e => ({ eupId: e.eupId, name: e.name })) || []
    });

    setSubscription((prev) => {
      if (!prev) {
        console.warn('⚠️ No subscription to add company to');
        return prev;
      }

      // Check if company already exists
      const existingCompany = prev.companies.find(c => c.companyId === company.companyId);
      if (existingCompany) {
        console.warn('⚠️ Company already exists in subscription, updating with new data');
        // Update existing company (może mieć nowe EUPs)
        return {
          ...prev,
          companies: prev.companies.map(c =>
            c.companyId === company.companyId ? company : c
          )
        };
      }

      const newSubscription = {
        ...prev,
        companies: [...prev.companies, company]
      };

      console.log('✅ Company added to Trial subscription locally. Total companies:', newSubscription.companies.length);
      return newSubscription;
    });
  }, []);

  /**
   * Unregister device and reset to Trial mode
   */
  const unregisterDevice = useCallback(async () => {
    console.log('🔄 Unregistering device...');
    try {
      if (!fingerprint) {
        throw new Error('No fingerprint available');
      }

      // Clear all localStorage first
      console.log('🧹 Clearing localStorage...');
      localStorage.clear();

      // Reset to Trial subscription
      const trialSubscription: DeviceSubscription = {
        subscriptionId: `trial-${fingerprint.substring(0, 8)}`,
        source: 'Portal',
        planId: 'carbowaste_bdo_trial',
        status: 'Trial',
        autoRenew: false,
        termUnit: 'Unlimited',
        purchaserEmail: 'trial@carbowaste.com',
        startDate: new Date().toISOString(),
        endDate: '9999-12-31T23:59:59Z',
        companies: [],
        limits: { companiesLimit: 1, eupsLimit: 9000 }
      };

      setSubscription(trialSubscription);
      console.log('✅ Device unregistered, switched to TRIAL mode');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('❌ Device unregistration failed:', errorMessage);
      throw err;
    }
  }, [fingerprint]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if device is registered (not in Trial mode)
   */
  const isRegistered = subscription !== null && subscription.status !== 'Trial';

  /**
   * Check if device is in Trial mode
   */
  const isTrial = subscription !== null && subscription.status === 'Trial';

  const contextValue: DeviceContextType = {
    fingerprint,
    subscription,
    userInfo,
    isLoading,
    error,
    isRegistered,
    isTrial,
    refreshSubscription,
    addCompanyToTrialSubscription,
    unregisterDevice,
    clearError,
  };

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
};

/**
 * Hook to use Device Context
 * @throws Error if used outside DeviceProvider
 */
export const useDevice = (): DeviceContextType => {
  const context = useContext(DeviceContext);

  if (context === undefined) {
    throw new Error('useDevice must be used within DeviceProvider');
  }

  return context;
};

/**
 * Export context for testing
 */
export { DeviceContext };
